import Vue from "vue";
import Vuex from "vuex";
import { App } from "@/store/types";
import { UserVO } from "@/types/REST-API-DOMAIN/UserVO";
import SubscriptionSignup from "@/store/modules/SubscriptionSignup";
import { ROLES } from "@/enums";
import TopNavModule from "../views/academy/topnav/TopNavModule";
import AuthModule from "../views/academy/auth/AuthModule";
import StorageModule from "../views/academy/storage/StorageModule";
import { ModuleTypes } from "@/store/ModuleTypes";
import RootViewModule from "@/views/academy/common/rootview/RootViewModule";
import CommonSnackbarModule from "@/views/academy/common/snackbar/CommonSnackbarModule";
import AcademyNavigatorModule from "@/views/academy/common/academynavigator/AcademyNavigatorModule";
import StorageItemDetailsModule from "@/views/academy/common/storageitemdetails/StorageItemDetailsModule";
import AssignmentsModule from "@/views/academy/assignments/AssignmentsModule";
import TodayModule from "@/views/Today/TodayModule";
import { getPlayersAndTeams, getUser } from "@/helpers/api";
import MutationTypes from "@/store/MutationTypes";
import ActionTypes from "@/store/ActionTypes";
import GetterTypes from "@/store/GetterTypes";
import TeamCalendarModule from "@/views/Calendar/TeamCalendarModule";
import TeamsModule from "@/views/Team/TeamsModule";
import IFrameModalModule from "@/store/modules/IFrameModalModule";
import ChatModule from "@/views/Chat/ChatModule";
import router from "@/router";
import { Routes } from "@/router/Routes";
import TeamRegistrationModule from "@/views/academy/teamregistration/TeamRegistrationModule";
import CreateRegistrationModule from "@/views/academy/createregistration/CreateRegistrationModule";
import PlayerRegistrationModule from "@/views/academy/playerregistration/PlayerRegistrationModule";
import EventRegistrationModule from "@/views/academy/eventregistration/EventRegistrationModule";
import PaymentsModule from "@/views/payments/PaymentsModule";
import { PermissionKeys, UserOrganizationsAndTeams } from "@/types";
import LogRocket from "logrocket";

Vue.use(Vuex);

const appState: App = {
  initStateLoaded: false,
  user: null,
  team: null,
  organization: null,
  playersAndTeams: null,
  navNotifications: [],
  alerts: []
};

const store: any = new Vuex.Store({
  state: appState,
  mutations: {
    [MutationTypes.SET_INIT_STATE_LOADED](state: App, val: boolean) {
      state.initStateLoaded = val;
    },
    [MutationTypes.SET_USER](state: App, user: UserVO) {
      state.user = user;
    },
    [MutationTypes.SET_PLAYERS_AND_TEAMS]: (state: App, data: any) => {
      state.playersAndTeams = data;
    },
    [MutationTypes.SET_NAV_NOTIFICATIONS]: (state: App, data: any) => {
      state.navNotifications = data;
    },
    [MutationTypes.SET_ALERTS]: (state: App, data: any) => {
      state.alerts = data;
    },
    [MutationTypes.SET_ORG_UNIT_SELECTION]: (
      state: App,
      data: {
        org: UserOrganizationsAndTeams | null;
        team: UserOrganizationsAndTeams | null;
      }
    ) => {
      state.organization = data.org;
      state.team = data.team;
    }
  },
  getters: {
    [GetterTypes.GET_NAV_NOTIFICATIONS]: (state: App) => {
      return state.navNotifications;
    },
    [GetterTypes.GET_ALERTS]: (state: App) => {
      return state.alerts;
    },
    [GetterTypes.GET_INIT_STATE_LOADED]: (state: App) => {
      return state.initStateLoaded;
    },
    [GetterTypes.GET_USER]: (state: App) => {
      return state.user;
    },
    [GetterTypes.GET_USER_IS_TEAMBOT_ADMIN]: (state: App) => {
      return state.user
        ? state.user.roles?.find(role => role === ROLES.ROLE_ADMIN)
        : null;
    },
    [GetterTypes.GET_TEAM]: (state: App) => {
      return state.team;
    },
    [GetterTypes.GET_PLAYERS_AND_TEAMS]: (state: App) => {
      return state.playersAndTeams;
    },
    [GetterTypes.GET_SHOW_NAVIGATION]: (state: App) => {
      return (
        state.playersAndTeams &&
        (state.playersAndTeams.following.length > 0 ||
          state.playersAndTeams.players.length > 0 ||
          state.playersAndTeams.organizations.length > 0)
      );
    },
    [GetterTypes.GET_PLAYERS_AND_TEAMS_VISIBLE]: (state: App) => {
      return (
        state.playersAndTeams &&
        state.playersAndTeams.organizations &&
        state.playersAndTeams.organizations.length > 0
      );
    },
    [GetterTypes.GET_USER_HAS_NO_PLAYERS_OR_TEAMS]: (state: App) => {
      return (
        (!state.playersAndTeams.organizations ||
          state.playersAndTeams.organizations.length === 0) &&
        (!state.playersAndTeams.players ||
          state.playersAndTeams.players.length === 0)
      );
    },
    [GetterTypes.GET_ORG_HAS_LIMITED_FUNCTIONALITY]: (state: App) => {
      const org = state.organization;
      if (org) {
        return org.limitedFunctionality;
      } else {
        return false;
      }
    },
    [GetterTypes.GET_ORG_SUBSCRIPTION_STATUS]: (state: App) => {
      const org = state.organization;
      if (org) {
        if (org.licenseSubscriptions?.length === 0) {
          return "EXPIRED";
        } else if (
          org?.licenseSubscriptions?.find(ls => ls.status === "GRACE_PERIOD")
        ) {
          return "GRACE_PERIOD";
        } else {
          return "ACTIVE";
        }
      } else {
        return null;
      }
    },
    [GetterTypes.GET_ORG_SUBSCRIPTION_STATUS_MESSAGE]: (state: App) => {
      const org = state.organization;
      if (org && !org.subscriptionOverride) {
        if (org.licenseSubscriptions?.length === 0) {
          return "Your subscription is expired";
        } else if (
          org.licenseSubscriptions?.length === 1 &&
          org.licenseSubscriptions?.find(ls => ls.status === "GRACE_PERIOD")
        ) {
          const licenseSubscription = org.licenseSubscriptions[0];
          const expiresIn =
            licenseSubscription.gracePeriodExpiresInHours / 24 > 1
              ? `${Math.floor(
                  licenseSubscription.gracePeriodExpiresInHours / 24
                )} days.`
              : `${Math.floor(
                  licenseSubscription.gracePeriodExpiresInHours
                )} hours.`;

          return `Your subscription will expire in  ${expiresIn} 
                  Please update the payment method for your subscription to continue using TeamBot.`;
        } else if (
          //@ts-ignore
          org?.licenseSubscriptions?.length > 1 &&
          org.licenseSubscriptions?.find(ls => ls.status === "GRACE_PERIOD")
        ) {
          const licenseSubscription = org.licenseSubscriptions.find(
            ls => ls.status === "GRACE_PERIOD"
          );

          if (
            org.activeTeamCount &&
            org.licenseCount &&
            org.activeTeamCount >
              org.licenseCount - licenseSubscription.licenseCount
          ) {
            const expiresIn =
              licenseSubscription.gracePeriodExpiresInHours / 24 > 1
                ? `${Math.floor(
                    licenseSubscription.gracePeriodExpiresInHours / 24
                  )} days.`
                : `${Math.floor(
                    licenseSubscription.gracePeriodExpiresInHours
                  )} hours.`;

            return (
              `${licenseSubscription.licenseCount} of your ${org.licenseCount} available licenses will expire in ` +
              expiresIn +
              ` Please update the payment method for your subscription or archive ${licenseSubscription.licenseCount} teams to continue using TeamBot.`
            );
          } else if (
            org.activeTeamCount &&
            org.licenseCount &&
            org.activeTeamCount > org.licenseCount
          ) {
            return `
              You have ${org.licenseCount} available licenses and ${
              org.activeTeamCount
            } active teams. Please archive ${org.activeTeamCount -
              org.licenseCount}
              teams or purchase additional licenses to continue using TeamBot.
            `;
          } else if (org.dataSubscriptionStatus !== "active") {
            return `
              The payment method on your usage subscription has expired.
              Please update your payment method to continue using TeamBot
            `;
          }
        }
      } else {
        return null;
      }
    },
    [GetterTypes.GET_ORGANIZATION]: (state: App) => {
      return state.organization;
    },
    [GetterTypes.GET_SELECTED_ORG_UNIT]: (state: App) => {
      return state.team || state.organization;
    },
    [GetterTypes.GET_COACH_HAS_PERMISSION]: (state: App) => (
      permission: PermissionKeys
    ) => {
      return (
        // @ts-ignore
        (state.organization && state.organization[permission]) ||
        // @ts-ignore
        (state.team && state.team[permission])
      );
    },
    [GetterTypes.GET_COACH_HAS_PERMISSION_WITH_ORG_ID]: (state: App) => (
      permission: PermissionKeys,
      orgId: string,
      teamId: string
    ) => {
      const org = state.playersAndTeams.organizations.find(
        (o: any) => o.id === orgId
      );
      return (
        org[permission] ||
        (teamId &&
          org.teams?.find((t: any) => t.id === teamId && t[permission]) != null)
      );
    }
  },
  actions: {
    async [ActionTypes.RELOAD_PLAYERS_AND_TEAMS](context) {
      const playersAndTeams = await getPlayersAndTeams();
      context.commit(MutationTypes.SET_PLAYERS_AND_TEAMS, playersAndTeams);
    },
    async [ActionTypes.INIT_GLOBAL_STATE](context) {
      const user: UserVO = await getUser();
      if (user.id) {
        const playersAndTeams: any = await getPlayersAndTeams();
        console.log(playersAndTeams);
        await context.commit(
          MutationTypes.SET_PLAYERS_AND_TEAMS,
          playersAndTeams
        );

        if (user.roles?.includes(ROLES.ROLE_TRACK)) {
          LogRocket.init("scsqti/teambot");
          LogRocket.identify(user.id, {
            name: user.firstName + " " + user.lastName
          });
        }

        // const alerts = await getAlerts();
        await context.commit(MutationTypes.SET_USER, user);
        // await context.commit(MutationTypes.SET_ALERTS, alerts);

        await context.commit(MutationTypes.SET_INIT_STATE_LOADED, true);
      } else {
        window.location.href = "/login";
      }
    }
  },
  modules: {
    [ModuleTypes.TODAY]: TodayModule,
    [ModuleTypes.SUBSCRIPTION_SIGNUP]: SubscriptionSignup,
    [ModuleTypes.ROOT]: RootViewModule,
    [ModuleTypes.TOP_NAV]: TopNavModule,
    [ModuleTypes.AUTH]: AuthModule,
    [ModuleTypes.STORAGE]: StorageModule,
    [ModuleTypes.SNACKBAR]: CommonSnackbarModule,
    [ModuleTypes.ACADEMY_NAVIGATOR]: AcademyNavigatorModule,
    [ModuleTypes.STORAGE_ITEM_DETAILS]: StorageItemDetailsModule,
    [ModuleTypes.ASSIGNMENTS]: AssignmentsModule,
    [ModuleTypes.CREATE_REGISTRATION]: CreateRegistrationModule,
    [ModuleTypes.TEAM_REGISTRATION]: TeamRegistrationModule,
    [ModuleTypes.EVENT_REGISTRATION]: EventRegistrationModule,
    [ModuleTypes.PLAYER_REGISTRATION]: PlayerRegistrationModule,
    [ModuleTypes.TEAM_CALENDAR]: TeamCalendarModule,
    [ModuleTypes.TEAMS]: TeamsModule,
    [ModuleTypes.I_FRAME_MODAL]: IFrameModalModule,
    [ModuleTypes.CHAT]: ChatModule,
    [ModuleTypes.PAYMENTS]: PaymentsModule
  }
});

router.beforeEach((to, from, next) => {
  const user = store.getters[GetterTypes.GET_USER];
  if (user && to.path !== Routes.LOGOUT.path) {
    getUser()
      .then((response: any) => {
        if (response.status && response.status === 403) {
          window.location.href = `${Routes.LOGIN.path}?redirectUri=${to.fullPath}`;
        } else {
          store.commit(MutationTypes.SET_USER, response);
          next();
        }
      })
      .catch(() => {
        window.location.href = `${Routes.LOGIN.path}?redirectUri=${to.fullPath}`;
      });
  } else {
    next();
  }
});

export default store;
